import { useCallback, useEffect, useState } from 'react'
import type { Device } from '@twilio/voice-sdk'
import { choisirHautParleurTelnyx, choisirMicroTelnyx, telnyxActif } from '@/lib/voix/telnyx'

/**
 * Hook pour énumérer les périphériques audio (mic, speaker)
 * et les piloter sur les téléphones de l'agent (Device Twilio, et Telnyx s'il
 * est connecté : le même choix vaut pour les deux).
 *
 * Le `twilioDevice` peut être null avant l'init — le hook reste fonctionnel
 * pour énumérer les périphériques même sans Twilio.
 */
export function useAudioDevices(twilioDevice: Device | null) {
  const [microphones, setMicrophones] = useState<MediaDeviceInfo[]>([])
  const [speakers, setSpeakers] = useState<MediaDeviceInfo[]>([])
  const [currentMicrophone, setCurrentMicrophone] = useState<string | null>(null)
  const [currentSpeaker, setCurrentSpeaker] = useState<string | null>(null)
  const [hasPermission, setHasPermission] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)

  const refreshDevices = useCallback(async () => {
    setIsRefreshing(true)
    try {
      const devices = await navigator.mediaDevices.enumerateDevices()
      const mics = devices.filter((d) => d.kind === 'audioinput')
      const spks = devices.filter((d) => d.kind === 'audiooutput')

      setMicrophones(mics)
      setSpeakers(spks)
      setHasPermission(mics.some((m) => m.label !== ''))

      if (twilioDevice?.audio) {
        try {
          const inputDevices = twilioDevice.audio.availableInputDevices
          const currentInput =
            Array.from(inputDevices.values()).find((d) => d.deviceId === 'default')
              ?.deviceId || null
          setCurrentMicrophone(currentInput)

          if (twilioDevice.audio.speakerDevices) {
            const outputDevices = twilioDevice.audio.speakerDevices.get()
            if (outputDevices.size > 0) {
              const currentOutput =
                Array.from(outputDevices.values())[0]?.deviceId || null
              setCurrentSpeaker(currentOutput)
            }
          }
        } catch (error) {
          console.warn('Could not get current audio devices from Twilio:', error)
        }
      }
    } catch (error) {
      console.error('Error enumerating devices:', error)
    } finally {
      setIsRefreshing(false)
    }
  }, [twilioDevice])

  useEffect(() => {
    void refreshDevices()
    const handler = () => {
      void refreshDevices()
    }
    navigator.mediaDevices.addEventListener('devicechange', handler)
    return () => navigator.mediaDevices.removeEventListener('devicechange', handler)
  }, [refreshDevices])

  const changeMicrophone = useCallback(
    async (deviceId: string) => {
      if (telnyxActif()) await choisirMicroTelnyx(deviceId)
      if (!twilioDevice?.audio?.setInputDevice) return
      await twilioDevice.audio.setInputDevice(deviceId)
      setCurrentMicrophone(deviceId)
    },
    [twilioDevice],
  )

  const changeSpeaker = useCallback(
    async (deviceId: string) => {
      if (telnyxActif()) choisirHautParleurTelnyx(deviceId)
      if (!twilioDevice?.audio?.speakerDevices?.set) {
        console.warn('Speaker selection not supported in this browser')
        return
      }
      await twilioDevice.audio.speakerDevices.set(deviceId)
      setCurrentSpeaker(deviceId)
    },
    [twilioDevice],
  )

  const requestPermission = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      stream.getTracks().forEach((t) => t.stop())
      await refreshDevices()
      return true
    } catch (error) {
      console.error('Error requesting microphone permission:', error)
      return false
    }
  }, [refreshDevices])

  return {
    microphones,
    speakers,
    currentMicrophone,
    currentSpeaker,
    changeMicrophone,
    changeSpeaker,
    refreshDevices,
    requestPermission,
    hasPermission,
    isRefreshing,
  }
}
